import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchComments, Comment } from '../../store/slices/commentsSlice';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import LoadingSpinner from '../UI/LoadingSpinner';

interface CommentListProps {
  entityType: 'project' | 'projectFile';
  entityId: string;
  isProjectOwner?: boolean;
  showRating?: boolean;
}

const CommentList: React.FC<CommentListProps> = ({
  entityType,
  entityId,
  isProjectOwner = false,
  showRating = false,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const entityKey = `${entityType}:${entityId}`;
  const commentsData = useAppSelector((state) => state.comments.byEntity[entityKey]);

  useEffect(() => {
    dispatch(fetchComments({ entityType, entityId }));
  }, [dispatch, entityType, entityId]);

  if (!commentsData) {
    return <LoadingSpinner size="md" text="Loading comments..." />;
  }

  if (commentsData.isLoading) {
    return <LoadingSpinner size="md" text="Loading comments..." />;
  }

  const comments = commentsData.items || [];
  const replies = commentsData.replies || {};

  // Separate top-level comments and replies
  const topLevelComments = comments.filter((comment) => !comment.parentId);
  const repliesMap = new Map<string, Comment[]>();

  Object.keys(replies).forEach((parentId) => {
    repliesMap.set(parentId, replies[parentId] || []);
  });

  // Also check comments array for replies
  comments.forEach((comment) => {
    if (comment.parentId) {
      const parentId = comment.parentId;
      if (!repliesMap.has(parentId)) {
        repliesMap.set(parentId, []);
      }
      repliesMap.get(parentId)!.push(comment);
    }
  });

  return (
    <div className="space-y-6">
      {user && (
        <div className="mb-6">
          <CommentForm
            entityType={entityType}
            entityId={entityId}
            showRating={showRating}
            onSuccess={() => {
              dispatch(fetchComments({ entityType, entityId }));
            }}
          />
        </div>
      )}

      {topLevelComments.length === 0 ? (
        <p className="text-[var(--color-subtext-color)] text-center py-8">
          {user ? 'No comments yet. Be the first to comment!' : 'No comments yet.'}
        </p>
      ) : (
        <div className="space-y-4">
          {topLevelComments.map((comment) => (
            <CommentItem
              key={comment.id || comment._id}
              comment={comment}
              replies={repliesMap.get(comment.id || comment._id || '') || []}
              entityType={entityType}
              entityId={entityId}
              isProjectOwner={isProjectOwner}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentList;
